import { setUser } from '@sentry/react';
import { type ReactElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';

import { ErrorSection } from '@/components/sections/error-section';
import { SplashScreen } from '@/components/splashes/splash-screen';
import { Button } from '@/components/ui/button';
import { useLogin } from '@/hooks/use-login';
import { useUser } from '@/hooks/use-user';
import { FullWidthConstrainedLayout } from '@/layouts/full-width-constrained-layout';

type AuthGuardProps = { children: ReactElement };

export function AuthGuard({ children }: AuthGuardProps): ReactElement {
  const { isLoading, isAuthenticated, error } = useAuth();
  const { login } = useLogin();
  const { t } = useTranslation('errors');
  const { user } = useUser();

  useEffect(() => {
    if (!(isLoading || isAuthenticated)) {
      login();
    }
  }, [isLoading, isAuthenticated, login]);

  useEffect(() => {
    if (user) {
      setUser({
        id: user.id,
        email: user.email,
        username: user.username,
      });
    } else {
      setUser(null);
    }
  }, [user]);

  return error ? (
    <div className='flex h-screen w-screen overflow-hidden'>
      <div className='flex grow flex-col overflow-y-auto'>
        <FullWidthConstrainedLayout>
          <ErrorSection
            action={<Button onClick={() => login()}>{t('Login')}</Button>}
            code={401}
            description={t('You need to be logged in to access this page')}
            title={t('Unauthorized')}
          />
        </FullWidthConstrainedLayout>
      </div>
    </div>
  ) : isAuthenticated && !isLoading ? (
    children
  ) : (
    <SplashScreen />
  );
}
